
import { memo, useCallback, useState } from "react";
import { Target, Zap } from "lucide-react";
import { SetData } from "../../../types";
import { EditSetModal } from "../modals/EditSetModal";
import { useOptimizedSetData } from "../hooks/useOptimizedSetData";
import { SetButton } from "../components/SetButton";

interface OptimizedIndividualSetEditingSectionProps {
  exerciseId: string;
  sets: number;
  setData: SetData[];
  reps: number;
  weight: number;
  onSetDataChange: (setIndex: number, field: 'reps' | 'weight', value: string) => void;
}

export const OptimizedIndividualSetEditingSection = memo(function OptimizedIndividualSetEditingSection({
  exerciseId,
  sets,
  setData,
  reps,
  weight,
  onSetDataChange
}: OptimizedIndividualSetEditingSectionProps) {
  const [selectedSet, setSelectedSet] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    setButtons,
    getCurrentSetData,
    handleSetDataChange
  } = useOptimizedSetData({
    exerciseId,
    sets,
    setData,
    reps,
    weight,
    onSetDataChange
  });

  // Handler ultra-optimizado para abrir modal
  const handleSetClick = useCallback((setIndex: number) => {
    console.log(`⚡ Ultra-fast modal opening for set ${setIndex}`);
    const setDataForModal = getCurrentSetData(setIndex);
    console.log(`📊 Ultra-fast data for modal:`, setDataForModal);
    
    setSelectedSet(setIndex);
    setIsModalOpen(true);
  }, [getCurrentSetData]);

  const handleModalClose = useCallback((open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setSelectedSet(null);
    }
  }, []);

  if (sets <= 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-[#293233]/10 to-[#253B3D]/10 border border-[#293233]/20 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white">Editar Serie Individual</h3>
      </div>
      
      {/* Botones de series individuales ultra-optimizados con persistencia */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {setButtons.map((set) => (
          <SetButton
            key={`${exerciseId}-set-${set.index}`}
            exerciseId={exerciseId}
            setIndex={set.index}
            reps={set.reps}
            weight={set.weight}
            isSyncing={set.isSyncing}
            lastSyncTime={set.lastSyncTime}
            onClick={() => handleSetClick(set.index)}
          />
        ))}
      </div>

      {/* Modal ultra-optimizado */}
      {selectedSet !== null && (
        <EditSetModal
          isOpen={isModalOpen}
          onOpenChange={handleModalClose}
          setIndex={selectedSet}
          setData={getCurrentSetData(selectedSet)}
          exerciseId={exerciseId}
          onSetDataChange={handleSetDataChange}
        />
      )}
    </div>
  );
});
